import { useCallback, useEffect, useState, useMemo, useRef } from 'react'
import { MotivationInterface } from '../../../../common/types/interfaces'
import { getSalesApi } from '../../../../salesApi/salesApi'
import { SEARCH_DEBOUNCE_MS } from '../../../../common/utils/constants'
import { useSelector } from 'react-redux'
import { selectCurrentCluster } from '../../../../store/slices/currentClusterSlice'

const initialValue: MotivationInterface = {
  name: '',
  description: '',
  costPerOrder: undefined,
  revenuePercent: undefined,
  bonusForOrdersCount: undefined,
  ordersCountTarget: undefined,
  bonusForRevenue: undefined,
  revenueTarget: undefined,
  metadata: undefined,
  rules: [],
}

export const useMotivation = () => {
  const [modalActive, setModalActive] = useState<boolean>(false)
  const [detailsModalActive, setDetailsModalActive] = useState<boolean>(false)
  const [edit, setEdit] = useState<boolean>(false)
  const [data, setData] = useState<MotivationInterface>(initialValue)
  const [list, setList] = useState<MotivationInterface[]>([])
  const [current, setCurrent] = useState<MotivationInterface>()
  const [loading, setLoading] = useState(true)
  const [searchQuery, setSearchQuery] = useState('')
  const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('')
  const debounceTimerRef = useRef<NodeJS.Timeout | number | null>(null)
  const [salesApi] = useState(getSalesApi())
  const currentCluster = useSelector(selectCurrentCluster)

  const getList = useCallback(async () => {
    try {
      const result = await salesApi.getMotivationList({ sort: 'name', order: 'ASC' })
      if (result) setList(result)
      setLoading(false)
    } catch (error) {
      setLoading(false)
    }
  }, [salesApi])

  const startCreate = () => {
    setCurrent(undefined)
    setData(initialValue)
    setEdit(false)
    setModalActive(true)
  }

  const startEdit = (el: MotivationInterface) => {
    setCurrent({ ...el })
    setEdit(true)
    setModalActive(true)
  }
  const startDuplicate = useCallback((sourceItem: MotivationInterface) => {
    const { id, createdAt, updatedAt, deleted, ...rest } = sourceItem

    const duplicatedRules =
      sourceItem.rules?.map((rule) => ({
        costPerOrder: rule.costPerOrder,
        revenuePercent: rule.revenuePercent,
        categoryIds: rule.categories?.map((c) => c.categoryId) || [],
        itemIds: rule.items?.map((i) => i.itemId) || [],
      })) || []

    const duplicatedItem: MotivationInterface = {
      ...rest,
      name: `${sourceItem.name} (копия)`,
      rules: duplicatedRules,
    }
    setCurrent(duplicatedItem)
    setEdit(false)
    setData(duplicatedItem)
    setModalActive(true)
  }, [])
  const showDetails = useCallback((el: MotivationInterface) => {
    setCurrent(el)
    setDetailsModalActive(true)
  }, [])

  const handleSearch = (value: string) => {
    setSearchQuery(value)

    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      setDebouncedSearchQuery(value)
    }, SEARCH_DEBOUNCE_MS)
  }

  const clearSearch = () => {
    setSearchQuery('')
    setDebouncedSearchQuery('')
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }
  }

  const filteredList = useMemo(() => {
    if (!debouncedSearchQuery.trim()) {
      return list
    }

    const query = debouncedSearchQuery.toLowerCase().trim()

    return list.filter((item) => {
      return (
        item.id?.toString().toLowerCase().includes(query) ||
        item.name?.toLowerCase().includes(query) ||
        item.description?.toLowerCase().includes(query)
      )
    })
  }, [list, debouncedSearchQuery])

  useEffect(() => {
    if (currentCluster) getList()
  }, [currentCluster, getList])

  useEffect(() => {
    if (!modalActive) {
      setData(initialValue)
      setEdit(false)
    }
  }, [modalActive])

  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current)
      }
    }
  }, [])

  return {
    getList,
    modalActive,
    setModalActive,
    startCreate,
    startEdit,
    startDuplicate,
    edit,
    data,
    setData,
    list: filteredList,
    detailsModalActive,
    setDetailsModalActive,
    showDetails,
    current,
    loading,
    searchQuery,
    handleSearch,
    clearSearch,
  }
}
